import { zodResolver } from '@hookform/resolvers/zod';
import {
  type AgentTargetDetail,
  type AgentTargetListItem,
  type CreateAgentTargetRequest,
  CreateAgentTargetRequestSchema,
  type UpdateAgentTargetRequest,
  UpdateAgentTargetRequestSchema,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzNumberInput,
  UzSelect,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useAgentOptions, useCreateAgentTarget, useUpdateAgentTarget } from '../use-agent-targets';

interface AgentTargetFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  agentTarget?: AgentTargetDetail | AgentTargetListItem | null;
}

// Targets are monthly only. The Period field is shown for context but is
// locked to Monthly (non-editable); Quarterly / Annual were removed.
const PERIOD_OPTIONS = [{ value: 'MONTHLY', label: 'Monthly' }] as const;

/**
 * Target basis picker. TURNOVER counts gross transaction value billed
 * to customers. REVENUE counts profit only (gross minus supplier net cost).
 * The Lead Inbox tile strip groups its progress views by basis. See ADR-0043.
 */
const BASIS_OPTIONS = [
  {
    value: 'TURNOVER',
    label: 'Turnover',
    description: 'Gross transaction value billed to customers.',
  },
  {
    value: 'REVENUE',
    label: 'Revenue (profit only)',
    description: 'Profit margin - gross minus supplier net cost.',
  },
] as const;

/// Effective dating (2026-06-30): the form no longer collects the marketing
/// destination, the period start/end dates, or the currency. The backend
/// derives the effective period (month-start), keeps exactly one active
/// target per agent, and stamps the default currency itself.
const CREATE_DEFAULTS: CreateAgentTargetRequest = {
  userId: '',
  period: 'MONTHLY',
  targetAmount: 0,
  // 2026-08-04. Default basis is TURNOVER (full booking value / TTV) - the
  // canonical target basis across reporting; REVENUE is deferred.
  targetBasis: 'TURNOVER',
};

/**
 * Field set shared by create + edit. Reads the form via context
 * (UzFormField → useFormContext), so it renders correctly inside either UzForm.
 */
const AgentTargetFields = ({
  pending,
  agentOptions,
}: {
  pending: boolean;
  agentOptions: ReadonlyArray<{ value: string; label: string }>;
}): React.JSX.Element => (
  <>
    <UzFormField name="userId">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Agent"
          required
          placeholder="Select an agent"
          options={agentOptions}
          value={(field.value as string) || undefined}
          onValueChange={field.onChange}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    {/* Target basis is asked up-front: "what does the amount you're about to
        type represent?" Switching to revenue (profit only) re-anchors the
        downstream Lead Inbox tiles to the margin view. */}
    <UzFormField name="targetBasis">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Target basis"
          required
          options={BASIS_OPTIONS as ReadonlyArray<{ value: string; label: string }>}
          value={(field.value as string) || 'TURNOVER'}
          onValueChange={(v) => field.onChange(v ?? 'TURNOVER')}
          hint="Pick what the target amount represents before entering it."
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    {/* Period is fixed to Monthly. Shown for context but non-editable - the
        effective-dating boundary is always the start of the current month. */}
    <UzFormField name="period">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Period"
          required
          options={PERIOD_OPTIONS as ReadonlyArray<{ value: string; label: string }>}
          value="MONTHLY"
          onValueChange={() => field.onChange('MONTHLY')}
          error={error}
          disabled
        />
      )}
    </UzFormField>
    <UzFormField name="targetAmount">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Target amount"
          required
          precision={2}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    {/* `targetLeads` (Lead target) was retired from the form per the
        2026-06-16 product decision - admins track quotas by amount, not by
        lead count. The column stays on the DB + schema for historical rows;
        the form just stops writing to it. */}
    <UzFormField name="notes">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Notes"
          placeholder="Optional"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const AgentTargetFormDrawer = ({
  open,
  onOpenChange,
  mode,
  agentTarget,
}: AgentTargetFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createAgentTarget = useCreateAgentTarget();
  const updateAgentTarget = useUpdateAgentTarget();
  const pending = createAgentTarget.isPending || updateAgentTarget.isPending;

  const agentOptionsQuery = useAgentOptions();
  const agentOptions = (agentOptionsQuery.data ?? []).map((u) => ({
    value: u.id,
    label: u.fullName,
  }));

  const createForm = useForm<CreateAgentTargetRequest>({
    resolver: zodResolver(CreateAgentTargetRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });

  const editDefaults: UpdateAgentTargetRequest | Record<string, never> = agentTarget
    ? {
        userId: agentTarget.user.id,
        period: 'MONTHLY',
        targetAmount: agentTarget.targetAmount,
        targetBasis: agentTarget.targetBasis ?? 'TURNOVER',
        // targetLeads is preserved silently for legacy rows so an edit
        // round-trip doesn't accidentally clear the historical value.
        targetLeads: agentTarget.targetLeads ?? undefined,
        notes: 'notes' in agentTarget ? (agentTarget.notes ?? undefined) : undefined,
      }
    : {};

  const updateForm = useForm<UpdateAgentTargetRequest>({
    resolver: zodResolver(UpdateAgentTargetRequestSchema),
    defaultValues: editDefaults,
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (agentTarget) {
      updateForm.reset({
        userId: agentTarget.user.id,
        period: 'MONTHLY',
        targetAmount: agentTarget.targetAmount,
        targetBasis: agentTarget.targetBasis ?? 'TURNOVER',
        targetLeads: agentTarget.targetLeads ?? undefined,
        notes: 'notes' in agentTarget ? (agentTarget.notes ?? undefined) : undefined,
      });
    }
  }, [open, isCreate, agentTarget, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createAgentTarget.mutateAsync({
      ...values,
      targetLeads: values.targetLeads ?? null,
      notes: values.notes?.trim() ? values.notes : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!agentTarget) return;
    await updateAgentTarget.mutateAsync({
      id: agentTarget.id,
      payload: {
        ...values,
        targetLeads: values.targetLeads ?? null,
        notes: values.notes?.trim() ? values.notes : null,
      },
    });
    onOpenChange(false);
  });

  const fields = <AgentTargetFields pending={pending} agentOptions={agentOptions} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={
        isCreate
          ? 'Add agent target'
          : `Edit target - ${agentTarget?.user.fullName ?? 'agent target'}`
      }
      description="Set the agent's sales target. The backend records it as the agent's new active target effective from the start of the current month and keeps the previous one as history."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
